import React, { useMemo, useState } from 'react'
import {
  ChartingTabs,
  CHARTING_TABS_DEMO_ITEMS,
  CHARTING_TABS_LIVE_ITEMS,
  CHARTING_TABS_PREVIEW_STATE_LABELS,
  CHARTING_TABS_PREVIEW_STATES,
  type ChartingTabsPreviewState,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ChartingTabsShowcase.css'

const ChartingTabsShowcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState(CHARTING_TABS_LIVE_ITEMS[0].id)
  const [highlightedState, setHighlightedState] =
    useState<ChartingTabsPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'charting-tabs-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="charting-tabs-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as ChartingTabsPreviewState,
                  )
                }
              >
                {CHARTING_TABS_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {CHARTING_TABS_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
    ],
    [highlightedState],
  )

  const activeLabel =
    CHARTING_TABS_LIVE_ITEMS.find((item) => item.id === activeTab)?.label ??
    'Tab'

  return (
    <>
      <UiPreviewCanvas
        title="Charting tabs"
        description="96px segments on a filled track; selected is raised, hover tints the idle tab."
        stageClassName="ui-preview__stage--wide charting-tabs-showcase__stage-wrap"
      >
        <div className={`charting-tabs-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Click a tab to switch panels.
            </p>
            <div className={`charting-tabs-showcase__panel`}>
              <ChartingTabs
                items={CHARTING_TABS_LIVE_ITEMS}
                value={activeTab}
                onChange={setActiveTab}
              />
              <div className={`charting-tabs-showcase__content`}>
                <h4 className="charting-tabs-showcase__content-title">
                  {activeLabel}
                </h4>
                <p className="charting-tabs-showcase__content-body">
                  Content for the selected charting tab appears here.
                </p>
              </div>
            </div>
          </section>

          <section className="charting-tabs-showcase__matrix">
            <h4 className="charting-tabs-showcase__section-title">States</h4>
            {CHARTING_TABS_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`charting-tabs-showcase__row${
                  previewState === highlightedState
                    ? ' charting-tabs-showcase__row--selected'
                    : ''
                }`}
              >
                <span className="charting-tabs-showcase__row-label">
                  {CHARTING_TABS_PREVIEW_STATE_LABELS[previewState]}
                </span>
                <ChartingTabs
                  items={CHARTING_TABS_DEMO_ITEMS}
                  previewState={previewState}
                />
              </div>
            ))}
          </section>

          <section className="charting-tabs-showcase__matrix">
            <h4 className="charting-tabs-showcase__section-title">Light and</h4>
            <p className="ui-showcase-live__note">
              Light (`blue-300` track) and (`blue-800` track).
            </p>
            {CHARTING_TABS_PREVIEW_STATES.map((previewState) => (
              <div
                key={`light-${previewState}`}
                className="charting-tabs-showcase__row"
              >
                <span className="charting-tabs-showcase__row-label">
                  Light · {CHARTING_TABS_PREVIEW_STATE_LABELS[previewState]}
                </span>
                <ChartingTabs
                  items={CHARTING_TABS_DEMO_ITEMS}
                  previewState={previewState}
                />
              </div>
            ))}
            {CHARTING_TABS_PREVIEW_STATES.map((previewState) => (
              <div
                key={`dark-${previewState}`}
                className="charting-tabs-showcase__row charting-tabs-showcase__row--dark"
              >
                <span className="charting-tabs-showcase__row-label">
                  Dark · {CHARTING_TABS_PREVIEW_STATE_LABELS[previewState]}
                </span>
                <ChartingTabs
                  items={CHARTING_TABS_DEMO_ITEMS}
                  previewState={previewState}
                />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Charting tabs"
        sections={propertySections}
      />
    </>
  )
}

export default ChartingTabsShowcase
